import { useState, useEffect, useCallback } from 'react';
import { vendorAPI, parserAPI, emailAPI, journalAPI, reconcileAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Buildings, Receipt, CurrencyInr, Envelope, ArrowsClockwise, MagnifyingGlass,
  CheckCircle, CaretRight, CaretLeft, Plus, Notebook, Bank, Download,
  Eye, Warning, Clock, Lightning, Funnel, Calendar, PencilSimple, Money, Trash,
} from '@phosphor-icons/react';

const fmt = (v) => formatCurrency(v || 0);

// Default vendor date window: historical data floor (Apr 2025) → today, so the full
// vendor history AND any freshly recorded (today-dated) payment are always in range.
function currentFYRange() {
  const end = new Date().toISOString().split('T')[0];
  return { start: '2025-04-01', end };
}

function PageHeader({ title, description }) {
  return (
    <div>
      <h1 className="text-2xl font-bold text-[#0F172A]">{title}</h1>
      {description && <p className="text-sm text-[#64748B] mt-1">{description}</p>}
    </div>
  );
}

// ─── VENDOR LEDGER (drill-down view) ────────────────────────────────────────
function VendorLedger({ vendor, onBack }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState(currentFYRange());
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [paySaving, setPaySaving] = useState(false);
  const [extraPaid, setExtraPaid] = useState(0);
  const [payForm, setPayForm] = useState({
    amount: '', payment_date: new Date().toISOString().split('T')[0],
    mode: 'bank_transfer', reference: '', notes: '',
  });

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorAPI.getInvoices(vendor.vendor_id, { start_date: dateRange.start, end_date: dateRange.end, page, page_size: 50 });
      setInvoices(res.data.invoices || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [vendor.vendor_id, page, dateRange]);

  const [view, setView] = useState('ledger');
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [editPay, setEditPay] = useState(null);
  const [savingPay, setSavingPay] = useState(false);

  const loadLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      const res = await vendorAPI.getLedger(vendor.vendor_id, { start_date: dateRange.start, end_date: dateRange.end });
      setLedger(res.data);
    } catch { toast.error('Failed to load ledger'); }
    finally { setLedgerLoading(false); }
  }, [vendor.vendor_id, dateRange]);

  const loadPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const res = await vendorAPI.getPayments(vendor.vendor_id, { start_date: dateRange.start, end_date: dateRange.end });
      setPayments(res.data.payments || (Array.isArray(res.data) ? res.data : []));
    } catch { toast.error('Failed to load payments'); }
    finally { setPaymentsLoading(false); }
  }, [vendor.vendor_id, dateRange]);

  useEffect(() => {
    if (view === 'invoices') loadInvoices();
    else if (view === 'ledger') loadLedger();
    else if (view === 'payments') loadPayments();
  }, [view, loadInvoices, loadLedger, loadPayments]);

  const exportLedgerCsv = () => {
    const rows = ledger?.ledger || [];
    if (!rows.length) { toast.error('Nothing to export'); return; }
    const head = ['Date', 'Type', 'Description', 'Reference', 'Debit', 'Credit', 'Balance'];
    const csv = [head.join(',')].concat(
      rows.map(e => [e.date, e.type, `"${(e.description || '').replace(/"/g, "'")}"`, `"${e.reference || ''}"`, e.debit || 0, e.credit || 0, e.balance || 0].join(','))
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(vendor.display_name || vendor.name || 'vendor').replace(/\s+/g, '_')}_ledger.csv`;
    a.click();
  };

  const exportPaymentsCsv = () => {
    if (!payments.length) { toast.error('Nothing to export'); return; }
    const head = ['Date', 'Reference', 'Mode', 'Notes', 'Amount'];
    const csv = [head.join(',')].concat(
      payments.map(p => [
        p.payment_date || p.date || '',
        `"${(p.reference || p.payment_number || '').replace(/"/g, "'")}"`,
        (p.mode || p.payment_mode || '').replace(/_/g, ' '),
        `"${(p.notes || '').replace(/"/g, "'")}"`,
        p.amount || 0,
      ].join(','))
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(vendor.display_name || vendor.name || 'vendor').replace(/\s+/g, '_')}_payments.csv`;
    a.click();
  };

  const handleDeletePayment = async (p) => {
    const pid = p.id || p.payment_id || p.zoho_id || p.payment_number;
    if (!pid) { toast.error('Cannot identify this payment'); return; }
    if (!window.confirm(`Delete this payment of ${fmt(p.amount)} dated ${p.payment_date || p.date}? This cannot be undone.`)) return;
    try {
      await vendorAPI.deletePayment(pid);
      toast.success('Payment deleted');
      loadPayments(); loadLedger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete payment'); }
  };

  const submitEditPay = async () => {
    const pid = editPay?.id || editPay?.payment_id || editPay?.zoho_id || editPay?.payment_number;
    const amt = parseFloat(editPay.amount) || 0;
    if (amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!editPay.payment_date) { toast.error('Payment date is required'); return; }
    setSavingPay(true);
    try {
      await vendorAPI.editPayment(pid, {
        amount: amt, payment_date: editPay.payment_date,
        mode: editPay.mode, reference: editPay.reference, notes: editPay.notes,
      });
      toast.success('Payment updated');
      setEditPay(null);
      loadPayments(); loadLedger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update payment'); }
    finally { setSavingPay(false); }
  };

  const submitPayment = async () => {
    const amt = parseFloat(payForm.amount) || 0;
    if (amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!payForm.payment_date) { toast.error('Payment date is required'); return; }
    setPaySaving(true);
    try {
      await vendorAPI.recordPayment(vendor.vendor_id, {
        amount: amt,
        payment_date: payForm.payment_date,
        mode: payForm.mode,
        reference: payForm.reference,
        notes: payForm.notes,
      });
      toast.success(`Payment of ${fmt(amt)} recorded for ${vendor.display_name || vendor.name}`);
      setExtraPaid(p => p + amt);
      setShowPay(false);
      // Ensure the just-recorded payment falls inside the active date window
      const payDate = payForm.payment_date;
      setDateRange(d => ({
        start: payDate < d.start ? payDate : d.start,
        end: payDate > d.end ? payDate : d.end,
      }));
      setPayForm({ amount: '', payment_date: new Date().toISOString().split('T')[0], mode: 'bank_transfer', reference: '', notes: '' });
      loadInvoices(); loadLedger(); loadPayments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record payment'); }
    finally { setPaySaving(false); }
  };

  const viewPdf = async (invoiceId) => {
    try {
      const res = await vendorAPI.getInvoicePdf(invoiceId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('PDF not available'); }
  };

  const paidTotal = (vendor.total_paid || 0) + extraPaid;
  const balanceTotal = (vendor.balance || 0) - extraPaid;

  return (
    <div className="space-y-4 p-6 sm:p-8" data-testid="vendor-ledger">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="vendor-back-btn"><CaretLeft size={18} /> Back</Button>
          <div>
            <h2 className="text-xl font-bold text-[#0F172A]">{vendor.display_name || vendor.name}</h2>
            <p className="text-sm text-[#64748B]">{vendor.type} — {vendor.invoice_count?.toLocaleString()} invoices ({vendor.parsed_count} parsed)</p>
          </div>
        </div>
        <Button className="bg-[#0F4C81] hover:bg-[#0F4C81] text-white" onClick={() => setShowPay(true)} data-testid="vendor-record-payment-btn">
          <Money size={16} className="mr-2" /> Record Payment
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="stats-card"><p className="text-xs text-[#64748B]">Total Invoiced (Parsed)</p><p className="text-xl font-bold">{fmt(vendor.total_invoiced)}</p></div>
        <div className="stats-card"><p className="text-xs text-[#64748B]">GST</p><p className="text-xl font-bold text-amber-600">{fmt(vendor.total_gst)}</p></div>
        <div className="stats-card"><p className="text-xs text-[#64748B]">Paid</p><p className="text-xl font-bold text-green-600">{fmt(paidTotal)}</p></div>
        <div className="stats-card"><p className="text-xs text-[#64748B]">Outstanding</p><p className={`text-xl font-bold ${balanceTotal > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(balanceTotal)}</p></div>
      </div>

      {/* View tabs */}
      <div className="flex items-center gap-1 border-b border-[#E2E8F0]">
        {[['ledger', 'Ledger'], ['invoices', 'Invoices'], ['payments', 'Payments']].map(([v, label]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`vendor-view-${v}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${view === v ? 'border-[#0F4C81] text-[#0F4C81]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Date filter */}
      <div className="flex items-end gap-3">
        <div><label className="text-xs text-[#64748B]">From</label><Input type="date" value={dateRange.start} onChange={e => setDateRange(d => ({...d, start: e.target.value}))} className="w-40" /></div>
        <div><label className="text-xs text-[#64748B]">To</label><Input type="date" value={dateRange.end} onChange={e => setDateRange(d => ({...d, end: e.target.value}))} className="w-40" /></div>
        <Button variant="outline" size="sm" onClick={() => { setPage(1); if (view==='invoices') loadInvoices(); else if (view==='ledger') loadLedger(); else loadPayments(); }}><Funnel size={14} className="mr-1" /> Filter</Button>
        {view === 'ledger' && <Button variant="outline" size="sm" onClick={exportLedgerCsv} data-testid="vendor-ledger-export"><Eye size={14} className="mr-1" /> Export CSV</Button>}
        {view === 'payments' && <Button variant="outline" size="sm" onClick={exportPaymentsCsv} data-testid="vendor-payments-export"><Download size={14} className="mr-1" /> Export CSV</Button>}
      </div>

      {/* Ledger view (true running-balance ledger) */}
      {view === 'ledger' && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto" data-testid="vendor-ledger-table">
          <div className="grid grid-cols-3 gap-4 p-4 border-b border-[#E2E8F0] bg-[#F8FAFC]">
            <div><p className="text-xs text-[#64748B]">Total Billed (Debit)</p><p className="text-lg font-bold">{fmt(ledger?.total_invoiced || 0)}</p></div>
            <div><p className="text-xs text-[#64748B]">Total Paid (Credit)</p><p className="text-lg font-bold text-green-600">{fmt(ledger?.total_paid || 0)}</p></div>
            <div><p className="text-xs text-[#64748B]">Closing Balance</p><p className={`text-lg font-bold ${(ledger?.closing_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(ledger?.closing_balance || 0)}</p></div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="px-3 py-2.5 text-left">Date</th>
                <th className="px-3 py-2.5 text-left">Description</th>
                <th className="px-3 py-2.5 text-left">Reference</th>
                <th className="px-3 py-2.5 text-right">Debit</th>
                <th className="px-3 py-2.5 text-right">Credit</th>
                <th className="px-3 py-2.5 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {ledgerLoading ? <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">Loading...</td></tr> :
                !ledger?.ledger?.length ? <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">No transactions in this period</td></tr> :
                  ledger.ledger.map((e, i) => (
                    <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                      <td className="px-3 py-2 font-mono text-xs">{e.date}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium mr-2 ${e.type === 'payment' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{e.type === 'payment' ? 'Payment' : 'Bill'}</span>
                        {e.description}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#64748B]">{e.reference || '—'}</td>
                      <td className="px-3 py-2 text-right text-xs">{e.debit ? fmt(e.debit) : '—'}</td>
                      <td className="px-3 py-2 text-right text-xs text-green-600">{e.credit ? fmt(e.credit) : '—'}</td>
                      <td className="px-3 py-2 text-right text-xs font-semibold">{fmt(e.balance)}</td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Payments view */}
      {view === 'payments' && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto" data-testid="vendor-payments-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="px-3 py-2.5 text-left">Date</th>
                <th className="px-3 py-2.5 text-left">Reference</th>
                <th className="px-3 py-2.5 text-left">Mode</th>
                <th className="px-3 py-2.5 text-left">Notes</th>
                <th className="px-3 py-2.5 text-right">Amount</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paymentsLoading ? <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">Loading...</td></tr> :
                !payments.length ? <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">No payments recorded</td></tr> :
                  payments.map((p, i) => (
                    <tr key={p.id || i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                      <td className="px-3 py-2 font-mono text-xs">{p.payment_date || p.date}</td>
                      <td className="px-3 py-2 text-xs">{p.reference || p.payment_number || '—'}</td>
                      <td className="px-3 py-2 text-xs capitalize">{(p.mode || p.payment_mode || '').replace(/_/g, ' ') || '—'}</td>
                      <td className="px-3 py-2 text-xs text-[#64748B] max-w-[280px] truncate">{p.notes || '—'}</td>
                      <td className="px-3 py-2 text-right text-xs font-semibold text-green-600">{fmt(p.amount)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" title="Edit"
                            onClick={() => setEditPay({ ...p, amount: p.amount, payment_date: p.payment_date || p.date || '', mode: p.mode || p.payment_mode || 'bank_transfer', reference: p.reference || p.payment_number || '', notes: p.notes || '' })}
                            data-testid={`vendor-pay-edit-${p.id || i}`}><PencilSimple size={14} /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" title="Delete"
                            onClick={() => handleDeletePayment(p)} data-testid={`vendor-pay-delete-${p.id || i}`}><Trash size={14} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Invoice table */}
      {view === 'invoices' && (
      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="vendor-invoices-table">
          <thead className="bg-[#F8FAFC]">
            <tr>
              <th className="px-3 py-2.5 text-left">Date</th>
              <th className="px-3 py-2.5 text-left">Invoice #</th>
              <th className="px-3 py-2.5 text-left">Subject</th>
              <th className="px-3 py-2.5 text-right">Subtotal</th>
              <th className="px-3 py-2.5 text-right">GST</th>
              <th className="px-3 py-2.5 text-right">Total</th>
              <th className="px-3 py-2.5 text-center">Status</th>
              <th className="px-3 py-2.5 text-center">PDF</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">Loading...</td></tr> :
              invoices.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">No invoices found</td></tr> :
                invoices.map(inv => (
                  <tr key={inv.invoice_id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                    <td className="px-3 py-2 font-mono text-xs">{inv.invoice_date}</td>
                    <td className="px-3 py-2 text-xs font-medium">{inv.invoice_number || '—'}</td>
                    <td className="px-3 py-2 text-xs text-[#64748B] max-w-[250px] truncate">{inv.email_subject || '—'}</td>
                    <td className="px-3 py-2 text-right text-xs">{inv.parsed ? fmt(inv.subtotal) : '—'}</td>
                    <td className="px-3 py-2 text-right text-xs text-amber-600">{inv.parsed ? fmt(inv.gst_amount) : '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold text-xs">{inv.parsed ? fmt(inv.total_amount) : <span className="text-[#999] italic">Unparsed</span>}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${inv.parsed ? 'bg-green-100 text-green-700' : inv.parse_status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {inv.parsed ? 'Parsed' : inv.parse_status === 'failed' ? 'Failed' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {inv.pdf_filename && (
                        <button onClick={() => viewPdf(inv.invoice_id)} className="text-[#0F4C81] hover:text-[#0F4C81]"><Eye size={16} /></button>
                      )}
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination */}
      {view === 'invoices' && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="text-sm text-[#64748B]">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Invoice Detail Dialog */}
      {selectedInvoice && (
        <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Invoice Detail</DialogTitle></DialogHeader>
            <pre className="text-xs bg-[#F8FAFC] p-3 rounded max-h-96 overflow-y-auto">{JSON.stringify(selectedInvoice.parsed_data, null, 2)}</pre>
          </DialogContent>
        </Dialog>
      )}

      {/* Record Vendor Payment Dialog */}
      <Dialog open={showPay} onOpenChange={setShowPay}>
        <DialogContent className="max-w-md" data-testid="vendor-payment-dialog">
          <DialogHeader><DialogTitle>Record Payment — {vendor.display_name || vendor.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Amount (₹) *</label>
                <Input type="number" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" data-testid="vendor-pay-amount" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Date *</label>
                <Input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} data-testid="vendor-pay-date" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Payment Mode</label>
              <Select value={payForm.mode} onValueChange={v => setPayForm(f => ({ ...f, mode: v }))}>
                <SelectTrigger data-testid="vendor-pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['bank_transfer', 'cash', 'upi', 'cheque', 'card'].map(m => <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Reference</label>
              <Input value={payForm.reference} onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))} placeholder="Txn / cheque no." data-testid="vendor-pay-ref" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Notes</label>
              <Input value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" data-testid="vendor-pay-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPay(false)} data-testid="vendor-pay-cancel">Cancel</Button>
            <Button className="bg-[#0F4C81] text-white" onClick={submitPayment} disabled={paySaving} data-testid="vendor-pay-submit">
              {paySaving ? 'Saving...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Vendor Payment Dialog */}
      <Dialog open={!!editPay} onOpenChange={(o) => !o && setEditPay(null)}>
        <DialogContent className="max-w-md" data-testid="vendor-payment-edit-dialog">
          <DialogHeader><DialogTitle>Edit Payment</DialogTitle></DialogHeader>
          {editPay && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">Amount (₹) *</label>
                  <Input type="number" value={editPay.amount} onChange={e => setEditPay(f => ({ ...f, amount: e.target.value }))} data-testid="vendor-pay-edit-amount" />
                </div>
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">Date *</label>
                  <Input type="date" value={editPay.payment_date} onChange={e => setEditPay(f => ({ ...f, payment_date: e.target.value }))} data-testid="vendor-pay-edit-date" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Payment Mode</label>
                <Select value={editPay.mode} onValueChange={v => setEditPay(f => ({ ...f, mode: v }))}>
                  <SelectTrigger data-testid="vendor-pay-edit-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['bank_transfer', 'cash', 'upi', 'cheque', 'card'].map(m => <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Reference</label>
                <Input value={editPay.reference} onChange={e => setEditPay(f => ({ ...f, reference: e.target.value }))} data-testid="vendor-pay-edit-ref" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Notes</label>
                <Input value={editPay.notes} onChange={e => setEditPay(f => ({ ...f, notes: e.target.value }))} data-testid="vendor-pay-edit-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPay(null)} data-testid="vendor-pay-edit-cancel">Cancel</Button>
            <Button className="bg-[#0F4C81] text-white" onClick={submitEditPay} disabled={savingPay} data-testid="vendor-pay-edit-submit">
              {savingPay ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── MAIN VENDOR PAGE ───────────────────────────────────────────────────────
export default function VendorManagementPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parseStats, setParseStats] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: '', display_name: '', type: '', description: '' });
  const [emailConfig, setEmailConfig] = useState({ email: '', app_password: '' });
  const [editVendor, setEditVendor] = useState(null);

  const loadVendors = useCallback(async () => {
    try {
      const res = await vendorAPI.getAll();
      setVendors(res.data.vendors || []);
    } catch { toast.error('Failed to load vendors'); }
    finally { setLoading(false); }
  }, []);

  const loadParseStats = useCallback(async () => {
    try {
      const res = await parserAPI.getStats();
      setParseStats(res.data);
    } catch {}
  }, []);

  const loadEmailStatus = useCallback(async () => {
    try {
      const res = await emailAPI.getStatus();
      setEmailStatus(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadVendors();
    loadParseStats();
    loadEmailStatus();
  }, [loadVendors, loadParseStats, loadEmailStatus]);

  // Auto-refresh parse stats every 30 seconds if parsing is in progress
  useEffect(() => {
    if (!parseStats?.running_job) return;
    const interval = setInterval(loadParseStats, 30000);
    return () => clearInterval(interval);
  }, [parseStats?.running_job, loadParseStats]);

  const handleSyncInvoices = async () => {
    setSyncing(true);
    try {
      const res = await emailAPI.syncInvoices({ since: '01-Apr-2025', until: '31-Mar-2026' });
      toast.success(res.data.message || 'Sync started');
    } catch (e) { toast.error('Sync failed'); }
    finally { setTimeout(() => setSyncing(false), 5000); }
  };

  const handleParseAll = async () => {
    setParsing(true);
    try {
      const res = await parserAPI.parseAll({ batch_size: 25 });
      toast.success(res.data.message || 'Parsing started');
      setTimeout(loadParseStats, 5000);
    } catch (e) { toast.error('Parse failed'); }
    finally { setTimeout(() => setParsing(false), 3000); }
  };

  const handleConfigureEmail = async () => {
    try {
      await emailAPI.configure(emailConfig);
      toast.success('Email connected');
      loadEmailStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to connect'); }
  };

  const handleGenerateJournalEntries = async () => {
    try {
      const res = await journalAPI.generateFromVendors();
      toast.success(res.data.message);
    } catch { toast.error('Failed to generate journal entries'); }
  };

  const handleReconcileWithBank = async () => {
    try {
      const res = await reconcileAPI.reconcileVendors();
      toast.success(`Matched ${res.data.matched} transactions. Reconciliation: ${res.data.reconciliation_rate}`);
    } catch { toast.error('Reconciliation failed'); }
  };

  const handleAddVendor = async () => {
    if (!newVendor.name || !newVendor.display_name || !newVendor.type) {
      toast.error('Name, display name, and type are required');
      return;
    }
    try {
      await vendorAPI.create(newVendor);
      toast.success('Vendor created');
      setShowAddVendor(false);
      setNewVendor({ name: '', display_name: '', type: '', description: '' });
      loadVendors();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Drill-down view
  if (selectedVendor) {
    return <VendorLedger vendor={selectedVendor} onBack={() => { setSelectedVendor(null); loadVendors(); loadParseStats(); }} />;
  }

  const totalInvoiced = vendors.reduce((s, v) => s + (v.total_invoiced || 0), 0);
  const totalPaid = vendors.reduce((s, v) => s + (v.total_paid || 0), 0);
  const totalBalance = vendors.reduce((s, v) => s + (v.balance || 0), 0);
  const totalInvoices = vendors.reduce((s, v) => s + (v.invoice_count || 0), 0);

  return (
    <div className="space-y-5 p-6 sm:p-8" data-testid="vendor-management-page">
      <PageHeader title="Vendor Management" description="Track vendor invoices, payments, and expenses (FY 2025-26)" />

      {/* ── Summary Stats ── */}
      <div className="grid grid-cols-5 gap-4">
        <div className="stats-card" data-testid="vendor-count-card">
          <div className="flex items-center gap-2"><Buildings size={18} className="text-[#64748B]" /><p className="stats-card-label">Vendors</p></div>
          <p className="text-2xl font-bold mt-1">{vendors.length}</p>
        </div>
        <div className="stats-card" data-testid="total-invoices-card">
          <div className="flex items-center gap-2"><Receipt size={18} className="text-[#64748B]" /><p className="stats-card-label">Email Invoices</p></div>
          <p className="text-2xl font-bold mt-1">{totalInvoices.toLocaleString()}</p>
        </div>
        <div className="stats-card" data-testid="total-invoiced-card">
          <div className="flex items-center gap-2"><CurrencyInr size={18} className="text-[#64748B]" /><p className="stats-card-label">Total Invoiced (Parsed)</p></div>
          <p className="text-2xl font-bold mt-1">{fmt(totalInvoiced)}</p>
        </div>
        <div className="stats-card" data-testid="total-paid-card">
          <p className="stats-card-label">Total Paid</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{fmt(totalPaid)}</p>
        </div>
        <div className="stats-card" data-testid="total-balance-card">
          <p className="stats-card-label">Outstanding</p>
          <p className={`text-2xl font-bold mt-1 ${totalBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(totalBalance)}</p>
        </div>
      </div>

      {/* ── Email Sync + Parse Actions ── */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Envelope size={24} className="text-[#64748B]" />
            <div>
              <h3 className="font-semibold text-[#0F172A]">Email Invoice Sync</h3>
              <p className="text-sm text-[#64748B]">
                {emailStatus?.configured
                  ? `Connected: ${emailStatus.email} ${emailStatus.last_sync ? `| Last sync: ${emailStatus.last_sync.slice(0, 16).replace('T', ' ')}` : ''}`
                  : 'Connect your invoice email to auto-import vendor invoices'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {emailStatus?.configured ? (
              <>
                <CheckCircle size={20} className="text-green-600" weight="fill" />
                <Button onClick={handleSyncInvoices} disabled={syncing} variant="outline" size="sm" data-testid="sync-invoices-btn">
                  <ArrowsClockwise size={14} className={`mr-1 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Sync Emails'}
                </Button>
                <Button onClick={handleParseAll} disabled={parsing} size="sm" data-testid="parse-all-btn">
                  <Lightning size={14} className={`mr-1 ${parsing ? 'animate-spin' : ''}`} />
                  {parsing ? 'Parsing...' : 'AI Parse All'}
                </Button>
                <div className="h-6 w-px bg-[#E2E8F0]" />
                <Button onClick={handleGenerateJournalEntries} variant="outline" size="sm" data-testid="gen-journal-btn">
                  <Notebook size={14} className="mr-1" /> Journal Entries
                </Button>
                <Button onClick={handleReconcileWithBank} variant="outline" size="sm" data-testid="reconcile-bank-btn">
                  <Bank size={14} className="mr-1" /> Reconcile Bank
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Input placeholder="invoices@hearclear.in" value={emailConfig.email} onChange={e => setEmailConfig({...emailConfig, email: e.target.value})} className="w-52" data-testid="email-input" />
                <Input type="password" placeholder="App Password" value={emailConfig.app_password} onChange={e => setEmailConfig({...emailConfig, app_password: e.target.value})} className="w-44" data-testid="app-password-input" />
                <Button onClick={handleConfigureEmail} data-testid="connect-email-btn">Connect</Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Parse Status Dashboard ── */}
      {parseStats && parseStats.total > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 space-y-4" data-testid="parse-stats">
          {/* Overall stats row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <div><p className="text-xs text-[#64748B]">Total Emails</p><p className="text-lg font-bold">{parseStats.total.toLocaleString()}</p></div>
              <div><p className="text-xs text-[#64748B]">AI Parsed</p><p className="text-lg font-bold text-green-600">{parseStats.parsed.toLocaleString()}</p></div>
              <div><p className="text-xs text-[#64748B]">Pending</p><p className="text-lg font-bold text-amber-600">{parseStats.pending.toLocaleString()}</p></div>
              {parseStats.failed > 0 && <div><p className="text-xs text-[#64748B]">Failed</p><p className="text-lg font-bold text-red-600">{parseStats.failed.toLocaleString()}</p></div>}
              <div><p className="text-xs text-[#64748B]">Parsed Today</p><p className="text-lg font-bold text-blue-600">{parseStats.parsed_today || 0}</p></div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-[#0F172A]">{parseStats.parse_rate}</span>
              <div className="w-48 bg-[#E2E8F0] rounded-full h-2.5">
                <div className="bg-green-500 h-2.5 rounded-full transition-all" style={{ width: parseStats.parse_rate }} />
              </div>
            </div>
          </div>

          {/* Running job indicator */}
          {parseStats.running_job && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <ArrowsClockwise size={16} className="text-blue-600 animate-spin" />
              <span className="text-sm text-blue-700 font-medium">
                Parsing in progress... {parseStats.running_job.progress ? `${parseStats.running_job.progress.success} parsed, ${parseStats.running_job.progress.remaining} remaining` : 'Started'}
              </span>
            </div>
          )}

          {/* Per-vendor breakdown */}
          {parseStats.vendor_breakdown && parseStats.vendor_breakdown.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {parseStats.vendor_breakdown.map(v => (
                <div key={v.vendor} className="bg-[#F8FAFC] rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm text-[#0F172A]">{v.vendor}</p>
                    <p className="text-xs text-[#64748B]">{v.parsed}/{v.total} parsed ({v.parse_rate}) — {v.pending} pending</p>
                    {v.total_amount > 0 && <p className="text-xs font-medium text-[#0F172A] mt-0.5">Invoiced: {fmt(v.total_amount)} (GST: {fmt(v.gst_amount)})</p>}
                  </div>
                  <div className="w-24 bg-[#E2E8F0] rounded-full h-1.5">
                    <div className="bg-green-500 h-1.5 rounded-full" style={{ width: v.parse_rate }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Vendor List ── */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[#0F172A]">Vendors</h3>
        <Dialog open={showAddVendor} onOpenChange={setShowAddVendor}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="add-vendor-btn"><Plus size={16} className="mr-2" /> Add Vendor</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Vendor</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Short Name (e.g. hear.com)" value={newVendor.name} onChange={e => setNewVendor({...newVendor, name: e.target.value})} />
              <Input placeholder="Display Name" value={newVendor.display_name} onChange={e => setNewVendor({...newVendor, display_name: e.target.value})} />
              <Input placeholder="Type (e.g. OEM Supplier, Marketing Agency)" value={newVendor.type} onChange={e => setNewVendor({...newVendor, type: e.target.value})} />
              <Input placeholder="Description" value={newVendor.description} onChange={e => setNewVendor({...newVendor, description: e.target.value})} />
            </div>
            <DialogFooter><Button onClick={handleAddVendor}>Create Vendor</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[#64748B]">Loading vendors...</div>
      ) : vendors.length === 0 ? (
        <div className="text-center py-12 text-[#64748B]">
          <Buildings size={48} className="mx-auto mb-3 opacity-50" />
          <p>No vendors yet. Sync from email or add manually.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {vendors.map(v => (
            <div
              key={v.vendor_id}
              className="bg-white border border-[#E2E8F0] rounded-lg p-5 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedVendor(v)}
              data-testid={`vendor-card-${v.vendor_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A]">{v.display_name || v.name}</h3>
                  <p className="text-sm text-[#64748B]">{v.type}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="p-1.5 h-7 w-7" onClick={(e) => { e.stopPropagation(); setEditVendor({ ...v }); }} data-testid={`edit-vendor-${v.vendor_id}`}>
                    <PencilSimple size={14} className="text-[#64748B]" />
                  </Button>
                  <CaretRight size={20} className="text-[#64748B]" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div>
                  <p className="text-xs text-[#64748B]">Total Invoiced (Parsed)</p>
                  <p className="text-sm font-semibold text-[#0F172A]">{v.total_invoiced > 0 ? fmt(v.total_invoiced) : <span className="text-amber-600">Parsing...</span>}</p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B]">Total Paid</p>
                  <p className="text-sm font-semibold text-green-600">{fmt(v.total_paid)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B]">Balance</p>
                  <p className={`text-sm font-semibold ${v.balance > 0 ? 'text-red-600' : v.total_invoiced === 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {v.total_invoiced > 0 ? fmt(v.balance) : 'Pending'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-[#64748B]">
                <span>{v.invoice_count?.toLocaleString()} emails</span>
                <span className={v.parsed_count === v.invoice_count ? 'text-green-600 font-medium' : 'text-amber-600 font-medium'}>
                  {v.parsed_count} / {v.invoice_count} parsed
                </span>
                {v.payment_count > 0 && <span>{v.payment_count} payments</span>}
              </div>
              {/* Mini progress bar */}
              <div className="mt-2 bg-[#E2E8F0] rounded-full h-1.5">
                <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${v.invoice_count > 0 ? (v.parsed_count / v.invoice_count * 100) : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Vendor Dialog */}
      <Dialog open={!!editVendor} onOpenChange={(o) => !o && setEditVendor(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="edit-vendor-dialog">
          <DialogHeader><DialogTitle>Edit Vendor</DialogTitle></DialogHeader>
          {editVendor && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">Short Name</label>
                  <Input value={editVendor.name || ''} onChange={e => setEditVendor({...editVendor, name: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">Display Name *</label>
                  <Input value={editVendor.display_name || ''} onChange={e => setEditVendor({...editVendor, display_name: e.target.value})} data-testid="edit-vendor-display-name" />
                </div>
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">Trade Name</label>
                  <Input value={editVendor.trade_name || ''} onChange={e => setEditVendor({...editVendor, trade_name: e.target.value})} data-testid="edit-vendor-trade-name" />
                </div>
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">Type</label>
                  <Input value={editVendor.type || ''} onChange={e => setEditVendor({...editVendor, type: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">GSTIN</label>
                  <Input value={editVendor.gstin || ''} onChange={e => setEditVendor({...editVendor, gstin: e.target.value})} data-testid="edit-vendor-gstin" />
                </div>
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">PAN</label>
                  <Input value={editVendor.pan || ''} onChange={e => setEditVendor({...editVendor, pan: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">Email</label>
                  <Input value={editVendor.contact_email || ''} onChange={e => setEditVendor({...editVendor, contact_email: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">Phone</label>
                  <Input value={editVendor.contact_phone || ''} onChange={e => setEditVendor({...editVendor, contact_phone: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-[#64748B] block mb-1">Address</label>
                  <Input value={editVendor.address || ''} onChange={e => setEditVendor({...editVendor, address: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">City</label>
                  <Input value={editVendor.city || ''} onChange={e => setEditVendor({...editVendor, city: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-[#64748B] block mb-1">State</label>
                  <Input value={editVendor.state || ''} onChange={e => setEditVendor({...editVendor, state: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-[#64748B] block mb-1">Description</label>
                  <Input value={editVendor.description || ''} onChange={e => setEditVendor({...editVendor, description: e.target.value})} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditVendor(null)}>Cancel</Button>
            <Button data-testid="edit-vendor-save" onClick={async () => {
              try {
                await vendorAPI.update(editVendor.vendor_id, editVendor);
                toast.success('Vendor updated');
                setEditVendor(null);
                loadVendors();
              } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
            }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
